/**
 * Classifies `recordStatutoryPayment`/`voidStatutoryPayment`'s raw
 * server-side error message (`apps/api/src/trpc/routers/payroll.ts`) into a
 * known failure class, so `StatutorySection`/`StatutoryRecordDialog` can
 * render a translated, locale-aware message instead of the raw English
 * string verbatim.
 *
 * Adversarial-review finding (C9 second round, #22415): `post_run_sections
 * .tsx`'s statutory-payment record/void handlers previously called
 * `setError(result.message)` / `setVoidError(result.message)` directly for
 * every non-`entitlement_required` failure -- rendering the server's raw
 * English message to every locale, including si/ta/id/fr users. This is the
 * exact same leak class `post_run_disbursement_error_classification.ts` (C8
 * second round, #22410) already fixed for the disbursement-file path; this
 * module applies the identical pattern here.
 *
 * Both `recordStatutoryPayment` and `voidStatutoryPayment` share several
 * literal guard messages (the lock-date and period-lock rejections are
 * produced by the exact same shared helpers, `assertNotLocked`/
 * `assertNotInPeriodLock`, `lock_date_service.ts`/`period_lock.ts`) so ONE
 * classifier covers both call sites rather than two near-duplicate ones.
 *
 * `assertMoneyWriteAllowed` (`apps/api/src/lib/assert_money_write_allowed.ts`)
 * runs BEFORE every other guard in both procedures and fires for ANY
 * OAuth/agent-channel caller (`ctx.actorSource === 'oauth'`) -- which is
 * every call this app makes, since WagePro-web is entirely OAuth-backed (see
 * `use_pay_run_post_actions.ts`'s module docblock). Its two messages
 * (`step_up_required`/`value_cap_exceeded`) were therefore a REAL, everyday
 * failure mode when this classifier was first written, not a rare edge case
 * -- an earlier version of this classifier omitted them entirely, which
 * would have made the single highest-probability real-world failure fall
 * through to the generic `'unknown'` copy (second-round adversarial-review
 * finding, C9 third round, #22415: an actionability regression vs. the raw
 * message this classifier exists to replace, since "try again" gives the
 * user zero hint that 2FA is required).
 *
 * UPDATE (#22829) -- `step_up_required` is no longer reached through THIS
 * message-matching path in normal operation. `oauth_statutory_filing.ts`'s
 * `recordStatutoryPayment`/`voidStatutoryPayment` now check
 * `isStepUpRequired(err)` -- a code-based check against
 * `data.ledgerCode === 'LEDGER_AUTH_MONEY_WRITE_STEP_UP_REQUIRED'` --
 * BEFORE either function falls through to the generic
 * `{ reason: 'error', message }` branch that reaches this classifier (see
 * that module's `recordStatutoryPayment`/`voidStatutoryPayment` doc
 * comments). The code-based check is strictly more reliable than this
 * file's exact-string match on the server's message (a message copy-edit
 * would silently break the match below, but not the ledgerCode check), so
 * it wins the race for every real occurrence of this error today. This
 * file's `step_up_required` branch is kept as a defense-in-depth fallback
 * for a wire-drift scenario -- `data.ledgerCode` dropped from the response
 * while the exact message string somehow survives intact -- not because it
 * is expected to fire in practice; do not delete it. `value_cap_exceeded` is
 * unaffected by #22829 and remains reached through this classifier exactly
 * as before -- there is no code-based check for it.
 *
 * Matches on stable substrings/prefixes of the server's literal messages
 * rather than full equality where the message embeds a variable (the
 * payment_type in the duplicate-guard message, the lock date and expected/
 * actual amounts in the lock-date and amount-mismatch messages), so a
 * variable value this classifier hasn't explicitly enumerated still matches
 * the right class. Any message that doesn't match a known class falls back
 * to `'unknown'` -- the caller renders a generic translated "something went
 * wrong" message for that case, never the raw string. This is a best-effort
 * classification, not an exhaustive mirror of every possible server error: a
 * future new error message on the server simply falls into `'unknown'`
 * until someone adds a class for it, rather than leaking English in the
 * meantime.
 */
export type StatutoryActionErrorClass =
  | 'not_found'
  | 'not_approved'
  | 'duplicate'
  | 'amount_mismatch'
  | 'locked_period'
  | 'already_voided'
  | 'void_unsupported'
  | 'reconciled'
  | 'step_up_required'
  | 'value_cap_exceeded'
  | 'audience_required'
  | 'bank_account_invalid'
  | 'unknown';

export function classify_statutory_error_message(
  message: string,
): StatutoryActionErrorClass {
  // `recordStatutoryPayment`'s NOT_FOUND ("Pay run not found") and
  // `voidStatutoryPayment`'s NOT_FOUND ("Statutory payment not found") both
  // collapse to the same generic "couldn't find this record" copy -- there
  // is no user action that differs between the two (refresh and retry,
  // either way).
  if (
    message === 'Pay run not found' ||
    message === 'Statutory payment not found'
  ) {
    return 'not_found';
  }
  if (
    message ===
    'Statutory payments can only be recorded against approved pay runs'
  ) {
    return 'not_approved';
  }
  if (/payment has already been recorded for this pay run/i.test(message)) {
    return 'duplicate';
  }
  if (message.startsWith('Amount does not match the computed')) {
    return 'amount_mismatch';
  }
  // Shared across recordStatutoryPayment and voidStatutoryPayment --
  // `assertNotLocked`'s two messages ("Your accounts are locked up to and
  // including ...") and `assertNotInPeriodLock`'s single message ("Cannot
  // post to a locked period") both represent the same user-facing concept:
  // this date falls in a closed accounting period.
  if (
    message.startsWith('Your accounts are locked up to and including') ||
    message === 'Cannot post to a locked period'
  ) {
    return 'locked_period';
  }
  if (message === 'This statutory payment has already been voided') {
    return 'already_voided';
  }
  if (
    message ===
    'This statutory payment was recorded before void support was added and cannot be voided automatically. Contact support for a manual correcting journal entry.'
  ) {
    return 'void_unsupported';
  }
  if (
    message ===
    'Cannot void a reconciled statutory payment. Unreconcile it from the bank first.'
  ) {
    return 'reconciled';
  }
  // `assertMoneyWriteAllowed` -- runs before every other guard in both
  // procedures, for every OAuth-channel caller (see module docblock).
  if (
    message ===
    'Step-up authentication required. Please complete two-factor verification before authorising money writes.'
  ) {
    return 'step_up_required';
  }
  if (
    message.startsWith('Transaction amount exceeds the per-token value cap of')
  ) {
    return 'value_cap_exceeded';
  }
  // `requireNonNullAudience` (`apps/api/src/trpc/init.ts`) -- an OAuth token
  // bound to no specific audience.
  if (
    message ===
    'Token must declare a specific audience for payroll self-service access'
  ) {
    return 'audience_required';
  }
  // `assertPayrollBankAccount` (wraps `assertPaymentSourceAccount`,
  // `payment_account_guard.ts`) -- the caller-supplied `bank_account_id` no
  // longer resolves to a legitimate payment-source account (e.g. deleted or
  // re-typed between the picker load and submit). The UI's own picker only
  // ever offers valid accounts, so this is only reachable via a stale
  // picker selection or a direct API call, same "defence-in-depth, not the
  // primary gate" status as the amount-mismatch case above -- but a real,
  // classifiable message all the same, not a case for 'unknown'.
  if (
    /^Payment account .+ not found$/.test(message) ||
    /is not a bank or cash account and cannot be used as a payment account$/.test(
      message,
    )
  ) {
    return 'bank_account_invalid';
  }
  return 'unknown';
}
